import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const DAYS = [
  ["0", "Lundi"], ["1", "Mardi"], ["2", "Mercredi"], ["3", "Jeudi"],
  ["4", "Vendredi"], ["5", "Samedi"], ["6", "Dimanche"],
];

export default function SchedulePanel() {
  const { settings, refresh } = useSettings();
  const [schedule, setSchedule] = useState(settings.schedule);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSchedule(settings.schedule), [settings]);

  const setDay = (key, patch) => setSchedule((s) => ({ ...s, [key]: { ...(s?.[key] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { schedule });
      await refresh();
      toast.success("Horaires enregistrés — le calendrier de réservation est à jour.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10" data-testid="schedule-panel">
      <h2 className="font-display text-3xl text-[var(--brand-dark)]">Planning & horaires</h2>
      <p className="mt-2 max-w-xl text-sm font-light text-[var(--brand-muted)]">
        Définissez vos journées et horaires de travail. Les créneaux proposés aux clientes sont générés automatiquement à partir de ces horaires, de la durée de chaque prestation et des rendez-vous déjà réservés.
      </p>

      <div className="mt-8 border border-black/5 bg-[var(--brand-bg)]">
        {DAYS.map(([key, label]) => {
          const day = schedule?.[key] || { open: "09:30", close: "17:00", closed: true };
          return (
            <div key={key} className="flex flex-wrap items-center gap-4 border-b border-black/5 px-6 py-4 last:border-0" data-testid={`schedule-day-${key}`}>
              <p className="w-28 font-display text-xl text-[var(--brand-dark)]">{label}</p>
              <label className="flex cursor-pointer items-center gap-2 text-xs uppercase tracking-[0.15em] text-[var(--brand-muted)]">
                <input type="checkbox" checked={!day.closed}
                       onChange={(e) => setDay(key, { closed: !e.target.checked })}
                       className="h-4 w-4 accent-[#C4839B]" data-testid={`schedule-open-${key}`} />
                {day.closed ? "Fermé" : "Ouvert"}
              </label>
              {!day.closed && (
                <div className="flex items-center gap-3">
                  <input type="time" value={day.open} onChange={(e) => setDay(key, { open: e.target.value })}
                         className="h-11 border border-black/10 bg-transparent px-3 text-sm font-light text-[var(--brand-dark)]"
                         data-testid={`schedule-open-time-${key}`} />
                  <span className="text-sm text-[var(--brand-muted)]">à</span>
                  <input type="time" value={day.close} onChange={(e) => setDay(key, { close: e.target.value })}
                         className="h-11 border border-black/10 bg-transparent px-3 text-sm font-light text-[var(--brand-dark)]"
                         data-testid={`schedule-close-time-${key}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving}
              className="sticky bottom-6 mt-8 rounded-full bg-[var(--brand-dark)] px-10 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] shadow-lg transition-transform duration-300 hover:scale-[0.97] disabled:opacity-50"
              data-testid="schedule-save-btn">
        {saving ? "Enregistrement…" : "Enregistrer les horaires"}
      </button>
    </div>
  );
}
